import type React from "react";
import {useState} from "react";
import type {UseQueryResult} from "@tanstack/react-query";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Skeleton,
    Stack,
    TextField,
    type Theme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "~/icons/svg/CloseIcon.svg?react";
import type {
    BuQualification,
    ComplianceRequirementFields,
    ComplianceRequirementWrite,
} from "~/pages/supplier-360/types/Supplier360Types";
import {useCreateComplianceRequirementMutation} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useCreateComplianceRequirementMutation";
import {useUpdateComplianceRequirementMutation} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useUpdateComplianceRequirementMutation";

/** The design's record types (`SXR_KINDS`). `kind` is free text in the API, so any value round-trips. */
const RECORD_KINDS = ['Certificate', 'Audit report', 'Declaration', 'Assessment', 'Questionnaire', 'Policy', 'Insurance'];

/** "Applies to" select value: the company layer, or a `bu_qualification` id. */
const COMPANY_WIDE = 'company_wide';

type ComplianceFormProps = {
    entityId: number;
    /**
     * The supplier's BU qualifications (`useSupplierBuQualificationsQuery`). The form shows a skeleton
     * while they load and an error with Retry if they fail — it can't save without them.
     */
    qualificationsQuery: Pick<UseQueryResult<BuQualification[]>, 'data' | 'isError' | 'isFetching' | 'refetch'>;
    /** The record being edited; omitted for "Add compliance record". */
    record?: ComplianceRequirementFields;
    onClose: () => void;
};

type FormValues = {
    name: string;
    kind: string;
    appliesTo: string;
    issuedAt: string;
    expiresAt: string;
};

function initialValues(record: ComplianceRequirementFields | undefined): FormValues {
    return {
        name: record?.name ?? '',
        kind: record?.kind || RECORD_KINDS[0],
        appliesTo: record?.bu_qualification != null ? String(record.bu_qualification) : COMPANY_WIDE,
        issuedAt: record?.issued_at?.slice(0, 10) ?? '',
        expiresAt: record?.expires_at?.slice(0, 10) ?? '',
    };
}

/**
 * `layer` + `bu_qualification` always travel together — the server rejects a mismatched pair. A
 * company-wide record is also linked to every BU of the supplier (`applies_to_ids`), which is what
 * the form promises ("satisfies all N business units") and what makes it show up under each BU
 * and count toward its status (§2.7, §2.8a). Only the BUs that exist now are linked.
 */
function layerFields(
    appliesTo: string,
    qualifications: BuQualification[],
): Pick<ComplianceRequirementWrite, 'layer' | 'bu_qualification' | 'applies_to_ids'> {
    return appliesTo === COMPANY_WIDE
        ? {layer: 'company_wide', bu_qualification: null, applies_to_ids: qualifications.map(q => q.id)}
        : {layer: 'business_unit', bu_qualification: Number(appliesTo)};
}

/** The form's three field rows and info note as placeholders, so the dialog keeps its size while loading. */
function formSkeleton() {
    const row = (key: string) => (
        <Stack key={key} direction={{xs: 'column', sm: 'row'}} gap={2}>
            <Skeleton variant="rounded" height={40} sx={{flex: 1}}/>
            <Skeleton variant="rounded" height={40} sx={{flex: 1}}/>
        </Stack>
    );
    return (
        <Stack gap={2} sx={{pt: 1}} aria-busy="true" aria-label="Loading business units">
            {['name', 'applies-to', 'validity'].map(row)}
            <Skeleton variant="rounded" height={52}/>
        </Stack>
    );
}

/**
 * "Add compliance record" (from scratch only) and "Edit compliance record" (`sup-records.jsx`).
 * The design's Issuing body and Verification fields have no writable backend field yet, so they are
 * commented out below. The fields wait behind a skeleton until the BU list has loaded — or an error
 * with Retry if it fails: it fills "Applies to", the info note's count, and a company-wide save's
 * `applies_to_ids`.
 */
export const ComplianceForm: React.FC<ComplianceFormProps> = ({
                                                                  entityId,
                                                                  qualificationsQuery,
                                                                  record,
                                                                  onClose,
                                                              }) => {
    // Full screen on phones, so the action buttons stay in reach.
    const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isEdit = record !== undefined;
    const qualifications = qualificationsQuery.data ?? [];
    const hasBus = qualificationsQuery.data !== undefined;
    // Failed with nothing to show — a retry in flight goes back to the skeleton.
    const isBusError = !hasBus && qualificationsQuery.isError && !qualificationsQuery.isFetching;
    const [values, setValues] = useState<FormValues>(() => initialValues(record));

    const createMutation = useCreateComplianceRequirementMutation();
    const updateMutation = useUpdateComplianceRequirementMutation();
    const isPending = createMutation.isPending || updateMutation.isPending;

    // Valid from is only on the add form, so that's the only place the range can be checked.
    // `yyyy-mm-dd` strings order the same as the dates they hold.
    const dateRangeError = !isEdit && values.issuedAt && values.expiresAt && values.expiresAt <= values.issuedAt
        ? 'Valid until must be after Valid from.'
        : undefined;

    // Record name and Valid until are required in both add and edit.
    // Never without the BU list: a company-wide save would link no BU.
    const isValid = hasBus && Boolean(values.name.trim()) && Boolean(values.expiresAt) && !dateRangeError;

    const kindOptions = RECORD_KINDS.includes(values.kind) ? RECORD_KINDS : [values.kind, ...RECORD_KINDS];
    const selectedBu = qualifications.find(q => String(q.id) === values.appliesTo);

    const set = (key: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) =>
        setValues(previous => ({...previous, [key]: event.target.value}));

    const submit = () => {
        if (!isValid || isPending) return;

        const name = values.name.trim();
        const issuedAt = values.issuedAt || null;
        const expiresAt = values.expiresAt;

        if (!isEdit) {
            createMutation.mutate(
                {
                    entity: entityId,
                    name,
                    kind: values.kind,
                    issued_at: issuedAt,
                    expires_at: expiresAt,
                    ...layerFields(values.appliesTo, qualifications),
                },
                {onSuccess: onClose},
            );
            return;
        }

        const original = initialValues(record);
        const patch: Partial<Omit<ComplianceRequirementWrite, 'entity'>> = {};
        if (name !== original.name) patch.name = name;
        if (values.kind !== original.kind) patch.kind = values.kind;
        if (values.appliesTo !== original.appliesTo) Object.assign(patch, layerFields(values.appliesTo, qualifications));
        if (values.issuedAt !== original.issuedAt) patch.issued_at = issuedAt;
        if (values.expiresAt !== original.expiresAt) patch.expires_at = expiresAt;

        if (Object.keys(patch).length === 0) {
            onClose();
            return;
        }
        updateMutation.mutate({id: record.id, patch}, {onSuccess: onClose});
    };

    const closeIfIdle = () => {
        if (!isPending) onClose();
    };

    const title = isEdit ? 'Edit compliance record' : 'Add compliance record';

    const validUntilField = (
        <TextField
            label="Valid until"
            type="date"
            value={values.expiresAt}
            size="small"
            fullWidth
            slotProps={{inputLabel: {shrink: true}}}
            required
            error={Boolean(dateRangeError)}
            helperText={dateRangeError}
            onChange={set('expiresAt')}
        />
    );

    return (
        <Dialog
            fullScreen={fullScreen}
            open
            onClose={closeIfIdle}
            fullWidth
            maxWidth={false}
            slotProps={{paper: {sx: {width: 800, maxWidth: '100%'}}}}
            aria-labelledby="compliance-form-title"
        >
            <DialogTitle id="compliance-form-title" sx={{pr: 6, fontSize: 16}}>
                {title}
                <IconButton
                    onClick={closeIfIdle}
                    title="Close"
                    size="small"
                    sx={{position: 'absolute', top: 12, right: 12}}
                >
                    <CloseIcon width={16} height={16}/>
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {isBusError ? (
                    <Alert
                        severity="error"
                        variant="outlined"
                        sx={{mt: 1}}
                        action={(
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => void qualificationsQuery.refetch()}
                                sx={{textTransform: 'none'}}
                            >
                                Retry
                            </Button>
                        )}
                    >
                        Could not load this supplier's business units, so the record can't be saved yet.
                    </Alert>
                ) : !hasBus ? formSkeleton() : (
                    <Stack gap={2} sx={{pt: 1}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                            <TextField
                                label="Record name"
                                value={values.name}
                                placeholder="e.g. ISO 9001:2015"
                                size="small"
                                fullWidth
                                autoFocus
                                required
                                onChange={set('name')}
                            />
                            <TextField select label="Type" value={values.kind} size="small" fullWidth onChange={set('kind')}>
                                {kindOptions.map(kind => <MenuItem key={kind} value={kind}>{kind}</MenuItem>)}
                            </TextField>
                        </Stack>

                        <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                            <TextField
                                select
                                label="Applies to"
                                value={values.appliesTo}
                                size="small"
                                fullWidth
                                onChange={set('appliesTo')}
                            >
                                <MenuItem value={COMPANY_WIDE}>Company-wide</MenuItem>
                                {qualifications.map(q => (
                                    <MenuItem key={q.id} value={String(q.id)}>{q.business_unit?.bu_name ?? `BU #${q.id}`}</MenuItem>
                                ))}
                            </TextField>
                            {/* Add shows Valid from first, then Valid until on the next row; edit has no Valid from. */}
                            {isEdit ? validUntilField : (
                                <TextField
                                    label="Valid from"
                                    type="date"
                                    value={values.issuedAt}
                                    size="small"
                                    fullWidth
                                    slotProps={{inputLabel: {shrink: true}}}
                                    onChange={set('issuedAt')}
                                />
                            )}
                            {/* Issuing body: no field on the compliance record (§2.8) — it lives on the evidence
                                (§2.9). Hidden until the backend adds one.
                            <TextField
                                label="Issuing body"
                                placeholder="e.g. Accredited certification body"
                                size="small"
                                fullWidth
                                disabled
                            />
                            */}
                        </Stack>

                        <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                            {isEdit ? null : validUntilField}
                            {/* Verification: the record's `verification_status` is read-only, taken from its latest
                                evidence (§2.8). Hidden until the backend makes it writable.
                            {isEdit ? (
                                <TextField select label="Verification" value="" size="small" fullWidth disabled>
                                    <MenuItem value=""/>
                                </TextField>
                            ) : null}
                            */}
                        </Stack>

                        <Alert severity="info" variant="outlined">
                            {values.appliesTo === COMPANY_WIDE
                                ? isEdit
                                    ? `Kept at company level: satisfies all ${qualifications.length} business units.`
                                    : `Company-wide records are collected once and satisfy all business units. The record opens as pending until evidence is uploaded.`
                                : `Scoped to ${selectedBu?.business_unit?.bu_name ?? 'this business unit'}. Only that business unit's qualification is affected.`}
                        </Alert>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button variant="outlined" disabled={isPending} onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={isPending || !isValid}
                    endIcon={isPending ? <CircularProgress size={16} color="inherit"/> : undefined}
                    onClick={submit}
                    sx={{textTransform: 'none'}}
                >
                    {isEdit ? 'Save changes' : 'Add record'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
